import type { FC } from 'react';
import { useEffect } from 'react';

import { useGameConfig } from '../../config/GameConfigProvider';
import { incrementUnreadPrivateMessageCounterAction } from '../../store/actions/incrementUnreadPrivateMessageCounterAction';
import { usePrivateChatOptionsFetcher } from '../../store/apiHooks/usePrivateChatFetcher';
import type { CoreStoreType } from '../../store/CoreStore';
import { useCoreDispatch } from '../../store/CoreStore';
import { useChatSlice } from '../../store/slices/chat/selectors';
import { chatActions } from '../../store/slices/chat/slice';
import { MessageType } from '../../store/slices/chat/types';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useGameStateSlice } from '../../store/slices/game/selectors';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { useSubtitlesSlice } from '../../store/slices/subtitles/selectors';
import { useIsSmallerThanWide } from '../../styles/hooks/media';
import { generateWelcomeMessage, LIVE_SUPPORT_WELCOME_MESSAGE } from '../../utils/chat';

import { ChatCommunicationController } from './chatCommunicationController';
import type { IChatCommunicationProcessor } from './types';

export const chatCommunicationController = new ChatCommunicationController();

interface ChatCommunicationControllerConnectorProps {
  gameStore: CoreStoreType;
  processors?: IChatCommunicationProcessor[];
}

export const ChatCommunicationControllerConnector: FC<ChatCommunicationControllerConnectorProps> = ({
  gameStore,
  processors,
}): null => {
  const dispatch = useCoreDispatch();
  const chatWS = useConfigSlice.env()?.chatWS;
  const JSESSIONID = useConfigSlice.JSESSIONID();
  const userId = useConfigSlice.playerConfig()?.userId;
  const tableId = useConfigSlice.tableId();
  const casinoId = useConfigSlice.casinoConfig()?.casinoId;
  const tableName = useConfigSlice.tableName();
  const showOtherMessages = useConfigSlice.showOtherMessages();
  const isSubtitlesAvailable = useSubtitlesSlice.isAvailable();
  const isSubtitlesEnabled = useSubtitlesSlice.isEnabled();
  const subtitlesEnabled = isSubtitlesAvailable && isSubtitlesEnabled;
  const isGamePaused = useGameStateSlice.getIsGamePaused();
  const isGameAbandoned = useGameStateSlice.getIsGameAbandoned();
  const lang = useSettingsSlice.currentLanguage();
  const isPrivateTable = useConfigSlice.isPrivateTable();
  const privateChatOptionsFetcher = usePrivateChatOptionsFetcher();
  const subLang = useSubtitlesSlice.lang(lang);
  const isChatEnabled = !useConfigSlice.isChatDisabled();
  const lastChatMessage = useChatSlice.getLastChatMessage();
  const chatSubscriptionState = useChatSlice.getChatSubscriptionState();
  const isSpectator = useConfigSlice.isSpectatorUser();
  const dealerName = useGameStateSlice.dealer()?.name;
  const {
    config: { isCrashGame },
  } = useGameConfig();
  const isMobile = useIsSmallerThanWide();

  useEffect(() => {
    if (tableName && dealerName) {
      const welcomeMessage = generateWelcomeMessage(Boolean(isCrashGame), tableName, dealerName, isPrivateTable);
      dispatch(chatActions.storeGameChatMessage(welcomeMessage));
      dispatch(chatActions.storeLastChatMessage(welcomeMessage));
      dispatch(chatActions.storeLiveSupportMessage(LIVE_SUPPORT_WELCOME_MESSAGE));
    }
  }, [dealerName, dispatch, isCrashGame, tableName, isPrivateTable]);

  useEffect(() => {
    if (processors) {
      processors.forEach((processor) => chatCommunicationController.setProcessor(processor));
    }

    chatCommunicationController.setStore(gameStore);
  }, [processors, gameStore]);

  useEffect(() => {
    if (JSESSIONID && tableId && userId && chatWS && casinoId && tableName) {
      if (isChatEnabled || subtitlesEnabled) {
        chatCommunicationController.connect(
          chatWS,
          JSESSIONID,
          userId,
          tableId,
          casinoId,
          tableName,
          `${userId}_${Date.now()}`,
          showOtherMessages,
          subtitlesEnabled,
          lang,
          subLang
        );
      } else {
        chatCommunicationController.close();
      }
    }
  }, [
    chatWS,
    JSESSIONID,
    userId,
    tableId,
    casinoId,
    tableName,
    showOtherMessages,
    subtitlesEnabled,
    lang,
    subLang,
    isChatEnabled,
  ]);

  useEffect(() => {
    if (chatSubscriptionState !== 'UNSUBSCRIBED') {
      chatCommunicationController.sendChatPreference({ subEnabled: subtitlesEnabled, subLang });
    }
  }, [chatSubscriptionState, subLang, subtitlesEnabled]);

  useEffect(() => {
    if (isPrivateTable) {
      const isHostMessage =
        lastChatMessage?.messageType === MessageType.Host && lastChatMessage?.messageId !== 'dealer_welcome_message';
      const isUserMessage = lastChatMessage?.messageType === MessageType.GameUser;

      if (isHostMessage || (isUserMessage && isSpectator)) {
        dispatch(incrementUnreadPrivateMessageCounterAction());
      }
    }
  }, [dispatch, isPrivateTable, isSpectator, lastChatMessage]);

  useEffect(() => {
    if (isPrivateTable) {
      privateChatOptionsFetcher();
    }
  }, [isPrivateTable, privateChatOptionsFetcher]);

  useEffect(() => {
    chatCommunicationController.updateConnectionOnGamePause(isGamePaused);
  }, [isGamePaused]);

  useEffect(() => {
    if (isGameAbandoned) {
      chatCommunicationController.close();
    }
  }, [isGameAbandoned]);

  useEffect(() => {
    if (isPrivateTable) {
      dispatch(chatActions.setIsPrivateOptionsExpanded(!isMobile));
    }
  }, [dispatch, isPrivateTable, isMobile]);

  return null;
};
